<script lang="ts">
	import type { Review } from '$lib/types/yotpo';
	import { onMount } from 'svelte';
	import ReviewCardV2 from '../parts/reviews/reviewCardV2.svelte';
	import PageGrid16 from '../components/pageGrid16.svelte';
	import Carousel from '$lib/carousel.svelte';
	import Button from '$lib/buttons/v3/button.svelte';
	import ArrowRight from '$lib/icons/arrowRight.svelte';
	import ArrowLeft from '$lib/icons/arrowLeft.svelte';
	import { fade, fly } from 'svelte/transition';
	import type { EmblaCarouselType } from 'embla-carousel';
	import ReviewSummaryCard from '../parts/reviews/reviewSummaryCard.svelte';
	import type { SbImage, VerifiedReview } from '../types';
	import type { ISbStoryData, SbBlokData } from '@storyblok/js';
	import { editable } from '../editable';

	interface ReviewInput extends SbBlokData {
		review_id: string;
		city: string;
		avatar: SbImage;
	}

	interface Props {
		content: ISbStoryData<{
			title: string;
			reviews: ReviewInput[];
		}>['content'];
	}

	let { content }: Props = $props();
	let idList = $derived(content.reviews.map((r) => parseInt(r.review_id)));
	let reviews: VerifiedReview[] = $state([]);
	let showAvatars = $state(false);

	let embla = $state<EmblaCarouselType>();
	let active = $state(0);

	function next() {
		if (embla) {
			embla.scrollNext();
		}
	}

	function previous() {
		if (embla) {
			embla.scrollPrev();
		}
	}

	async function getReviews(): Promise<Review[]> {
		let page = 1;

		let res = await fetch('/api/yotpo/reviews');
		let apiRes = await res.json();

		if (idList.length === 0) {
			return apiRes.reviews.slice(0, 5);
		} else {
			let foundReviews = apiRes.reviews.filter((r: Review) => idList.includes(r.id));

			while (foundReviews.length < idList.length && apiRes.hasMore) {
				page++;
				res = await fetch(`/api/yotpo/reviews?page=${page}`);
				apiRes = await res.json();
				foundReviews.push(...apiRes.reviews?.filter((r: Review) => idList.includes(r.id)));
			}

			return foundReviews.sort((a: Review, b: Review) => {
				return idList.indexOf(a.id) - idList.indexOf(b.id);
			});
		}
	}

	onMount(async () => {
		const apiReviews = await getReviews();

		reviews = apiReviews.map((apiReview) => {
			const sbReview = content.reviews.find((r) => parseInt(r.review_id) === apiReview.id);

			if (!showAvatars && sbReview?.avatar?.filename) {
				showAvatars = true;
			}

			return {
				score: apiReview.score,
				title: apiReview.title,
				content: apiReview.content,
				name: apiReview.user.display_name,
				verified: apiReview.verified_buyer,
				city: sbReview?.city,
				avatar: sbReview?.avatar
			};
		});
	});
</script>

<section use:editable={content} class="my-8 min-h-[450px] lg:my-20">
	<PageGrid16 margin>
		<h1
			class="col-span-full mb-10 text-[36px]/[112%] tracking-[-.36px] lg:col-start-2 lg:mb-12 lg:text-[48px]/[120%] lg:tracking-[-.48px]"
		>
			{content.title}
		</h1>
	</PageGrid16>

	<div class="relative w-screen overflow-x-hidden">
		<PageGrid16 margin>
			<Carousel
				bind:embla
				bind:currentIndex={active}
				containerClass="col-span-full lg:col-start-2 lg:mr-3 mb-3"
				autoplay={false}
				overflow
				class="flex gap-x-3 lg:gap-x-4"
				breakpoints={{ '(min-width: 1024px)': { slidesToScroll: 3 } }}
			>
				{#each reviews as review}
					<div in:fade class="flex-none basis-[75vw] lg:max-w-[407px] lg:basis-[25vw]">
						<ReviewCardV2 {review} showAvatar={showAvatars} />
					</div>
				{/each}
				{#if reviews.length > 0}
					<div class="flex-none basis-[75vw] lg:max-w-[407px] lg:basis-[25vw]">
						<ReviewSummaryCard />
					</div>
				{/if}
			</Carousel>
		</PageGrid16>
		<div class="max-lg:hidden">
			{#if active > 0}
				<div transition:fly={{ x: '-100%' }} class="absolute left-5 top-1/2 -translate-y-1/2">
					<Button
						onclick={previous}
						class="btn-white shadow-[0_4px_8px_0_rgba(0,0,0,0.08)]"
						title="Previous reviews"><ArrowLeft /></Button
					>
				</div>
			{/if}
			{#if active < reviews.length / 3 - 1}
				<div transition:fly={{ x: '100%' }} class="absolute right-5 top-1/2 -translate-y-1/2">
					<Button
						onclick={next}
						class="btn-white shadow-[0_4px_8px_0_rgba(0,0,0,0.08)]"
						title="Next reviews"><ArrowRight /></Button
					>
				</div>
			{/if}
		</div>
	</div>
</section>
